import type { ComponentType } from 'react';
import type { Capability } from '@dpem/contracts';
import {
  CalendarRangeIcon,
  GitCompareArrowsIcon,
  LibraryIcon,
  NetworkIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SquarePenIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';

import { PATHS } from './paths';

/**
 * The six surfaces, and how the shell presents them.
 *
 * This file is the seam between WP12 (the shell) and WP13–WP17 (the surfaces).
 * The shell owns the navigation — labels, order, icons, which capability gates an
 * entry — because navigation is chrome and has to be consistent across surfaces
 * that five separate work packages build. Each surface owns only its own tree.
 *
 * A surface is discovered, not registered. `import.meta.glob` matches
 * `src/surfaces/<key>/surface.tsx`, so a surface arrives by existing: there is no
 * central list for five parallel work packages to edit, and therefore no file
 * they all conflict in. The contract a surface must satisfy is one line —
 * default-export a React component — and it is checked at load time with a real
 * error rather than a blank screen.
 */

export const SURFACE_KEYS = [
  'explorer',
  'authoring',
  'compare',
  'workshop',
  'plans',
  'admin',
] as const;
export type SurfaceKey = (typeof SURFACE_KEYS)[number];

export interface NavEntry {
  readonly id: string;
  readonly label: string;
  readonly to: string;
  /** Which surface renders it. Several entries may point into one surface. */
  readonly surface: SurfaceKey;
  /**
   * Hidden unless the user holds this. `method:read` is the floor rather than
   * `undefined`, so no entry is unintentionally public.
   */
  readonly capability: Capability;
  readonly icon: LucideIcon;
  /** Screen-reader detail for the icon rail, where the label is not visible. */
  readonly description: string;
}

/**
 * Order is the order in the header and the rail, and it is a claim about the
 * product: explore before author, compare before adopt, adopt before plan. Admin
 * is last because it is the only entry that is not method work.
 */
export const NAV_ENTRIES: readonly NavEntry[] = [
  {
    id: 'explorer',
    label: 'Explorer',
    to: PATHS.explorer,
    surface: 'explorer',
    capability: 'method:read',
    icon: NetworkIcon,
    description: 'Browse a published method: phases, stages, activities, roles and gates',
  },
  {
    id: 'authoring',
    label: 'Authoring',
    to: PATHS.authoring,
    surface: 'authoring',
    capability: 'method:author',
    icon: SquarePenIcon,
    description: 'Compose and edit method versions on the canvas',
  },
  {
    id: 'compare',
    label: 'Compare',
    to: PATHS.compare,
    surface: 'compare',
    capability: 'method:read',
    icon: GitCompareArrowsIcon,
    description: 'Standard against agentic, element by element',
  },
  {
    id: 'workshop',
    label: 'Adoption workshop',
    to: PATHS.workshop,
    surface: 'workshop',
    capability: 'workshop:facilitate',
    icon: UsersIcon,
    description: 'Facilitate a tailoring session and publish the tailored method',
  },
  {
    id: 'plans',
    label: 'Plans',
    to: PATHS.plans,
    surface: 'plans',
    /*
     * `method:read`, not a planning capability of its own: every planning *read*
     * route is `ANY_ROLE`, so a reviewer who can see a method can see the plans
     * generated from it. Generating and curating gate on `plan:manage`, which the
     * surface checks per affordance — hiding the whole entry from a reviewer would
     * hide the plan they are reviewing against.
     */
    capability: 'method:read',
    icon: CalendarRangeIcon,
    description:
      'Generate a work breakdown from a method and a plan template: sequenced and parallel tasks, owners, durations, dependencies, training and setup',
  },
  {
    id: 'catalogue',
    label: 'Catalogue',
    to: PATHS.catalogue,
    surface: 'authoring',
    capability: 'catalog:manage',
    icon: LibraryIcon,
    description: 'Tools, assets, agents, agent skills, MCP servers and deliverable templates',
  },
  {
    id: 'governance',
    label: 'Governance',
    to: PATHS.governance,
    surface: 'authoring',
    capability: 'method:review',
    icon: ShieldCheckIcon,
    description: 'Review queue, publication history and the audit trail',
  },
  {
    id: 'admin',
    label: 'Admin',
    to: PATHS.admin,
    surface: 'admin',
    capability: 'admin:manage',
    icon: SettingsIcon,
    description: 'Platform settings, AI provider credentials and accounts',
  },
];

/**
 * The nav entry a pathname is inside — longest match, so `/authoring/catalogue`
 * highlights Catalogue rather than Authoring. A plain `startsWith` scan would
 * mark both current, and `aria-current="page"` on two links is a lie to a screen
 * reader as well as a visual bug.
 */
export const currentNavEntry = (
  pathname: string,
  entries: readonly NavEntry[] = NAV_ENTRIES,
): NavEntry | null => {
  let best: NavEntry | null = null;
  for (const entry of entries) {
    const matches = pathname === entry.to || pathname.startsWith(`${entry.to}/`);
    if (matches && (best === null || entry.to.length > best.to.length)) best = entry;
  }
  return best;
};

/**
 * Surface modules, discovered at build time. Vite turns this into a static map of
 * dynamic imports, so each surface is still its own chunk — the glob is a
 * discovery mechanism, not a bundling one.
 */
const SURFACE_MODULES: Readonly<Record<string, () => Promise<unknown>>> = import.meta.glob(
  '../surfaces/*/surface.tsx',
);

const modulePathFor = (key: SurfaceKey): string => `../surfaces/${key}/surface.tsx`;

/** Which surfaces are present. Used by tests and by the shell's fallback route. */
export const availableSurfaces = (): readonly SurfaceKey[] =>
  SURFACE_KEYS.filter((key) => modulePathFor(key) in SURFACE_MODULES);

export class SurfaceContractError extends Error {
  constructor(key: SurfaceKey, reason: string) {
    super(`Surface '${key}' ${reason}`);
    this.name = 'SurfaceContractError';
  }
}

/**
 * Load a surface's root component.
 *
 * Throws rather than returning a placeholder when the module is present but
 * malformed: a surface that exports the wrong thing is a build mistake, and the
 * router's error element reports it with the surface named. A *missing* module is
 * different and is handled by the caller — during Phase 4 the shell is built
 * before the surfaces it hosts.
 */
export const loadSurface = async (key: SurfaceKey): Promise<ComponentType> => {
  const loader = SURFACE_MODULES[modulePathFor(key)];
  if (loader === undefined) throw new SurfaceContractError(key, 'has not been built yet');

  const module = (await loader()) as { default?: unknown };
  if (typeof module.default !== 'function') {
    throw new SurfaceContractError(key, 'must default-export a React component');
  }
  return module.default as ComponentType;
};
